<template>
    <div class="page_wrap">
        <div class="top-card">
            <div class="nav_title">企业与成员</div>
            <div class="content-box">
                <!-- 企业信息展示 -->
                <div class="enterprise-info">
                    <div class="enterprise-name">
                        <span class="label">企业名称：</span>
                        <span class="value">{{ enterpriseInfo.company_name || '--' }}</span>
                        <span class="member-count">共{{ totalMembers }}名成员, {{ activeMembers }}名在职</span>
                    </div>
                    <div class="legal-person">
                        <span class="label">法定代表人：</span>
                        <span class="value">{{ enterpriseInfo.legal_person || '--' }}</span>
                    </div>
                </div>

                <!-- 搜索栏 -->
                <div class="search-bar">
                    <Input
                        v-model="searchKeyword"
                        placeholder="名称、手机号搜索"
                        style="width: 260px"
                        clearable
                        @on-enter="handleSearch"
                    />
                    <Select v-model="searchStatus" style="width: 160px">
                        <Option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                        </Option>
                    </Select>
                    <Button type="primary" @click="handleSearch">搜索</Button>
                </div>

                <!-- 添加成员按钮 -->
                <div class="table-header">
                    <Button type="primary" @click="showAddMember = true">添加成员</Button>
                </div>

                <!-- 成员列表表格 -->
                <Table :columns="columns" :data="memberList" :loading="loading">
                    <template slot-scope="{ index }" slot="index">
                        {{ (page - 1) * pageSize + index + 1 }}
                    </template>
                    <template slot-scope="{ row }" slot="action">
                        <Button type="error" v-if="row.status === 1" size="small" @click="resignMember(row)">离职</Button>
                    </template>
                </Table>

                <!-- 分页 -->
                <Page 
                    :total="total" 
                    :current="page" 
                    :page-size="pageSize" 
                    @on-change="changePage"
                    style="margin-top: 20px; text-align: right"
                    show-total
                    show-elevator
                />
            </div>
        </div>

        <!-- 添加成员弹窗 -->
        <Modal
            v-model="showAddMember"
            title="添加成员"
            :mask-closable="false"
            @on-cancel="handleCancel"
            @on-ok="handleAddMember"
        >
            <Form ref="addMemberForm" :model="addMemberForm" :rules="addMemberRules" :label-width="100">
                <FormItem label="成员姓名" prop="name"> 
                    <Input 
                        v-model="addMemberForm.name" 
                        placeholder="请输入成员姓名" 
                        style="width: 300px"
                        clearable
                    />
                </FormItem>
                <FormItem label="手机号" prop="mobile">
                    <Input 
                        v-model="addMemberForm.mobile" 
                        placeholder="请输入手机号" 
                        style="width: 300px"
                        clearable
                    />
                </FormItem>
            </Form>
        </Modal>
    </div>
</template>

<script>
import { get_enterprise_info, getMemberList, addMember, upMemberStatus } from '@/api/interFace'

export default {
    name: 'enterprise-members',
    data() {
        // 手机号验证规则
        const validateMobile = (rule, value, callback) => {
            if (!value) {
                callback()
            } else if (!/^1[3-9]\d{9}$/.test(value)) {
                callback(new Error('请输入正确的手机号'))
            } else {
                callback()
            }
        }

        return {
            loading: false,
            page: 1,
            pageSize: 10,
            total: 0,
            totalMembers: 0, // 总成员数
            activeMembers: 0, // 在职成员数
            memberList: [],
            showAddMember: false,
            enterpriseInfo: {},
            enterpriseAuthId: '',
            searchKeyword: '',
            searchStatus: 0, // 0全部 1在职 2离职
            statusOptions: [
                { value: 0, label: '全部' },
                { value: 1, label: '在职' },
                { value: 2, label: '离职' }
            ],
            addMemberForm: {
                name: '',
                mobile: ''
            },
            addMemberRules: {
                name: [
                    { required: true, message: '请输入成员姓名', trigger: 'blur' }
                ],
                mobile: [                    
                    { required: true, message: '请输入手机号', trigger: 'blur' },
                    { validator: validateMobile, trigger: 'blur' }
                ]
            },
            columns: [
                {
                    title: '#',
                    slot: 'index',
                    align: 'center',
                    width: 80
                },
                {
                    title: '成员名称',
                    key: 'real_name',
                    align: 'center'
                },
                {
                    title: '手机号',
                    key: 'phone',
                    align: 'center'
                },
                {
                    title: '状态',
                    key: 'status',
                    align: 'center',
                    width: 100,
                    render: (h, params) => {
                        const status = params.row.status
                        const text = status === 1 ? '在职' : '离职'
                        const color = status === 1 ? '#17233d' : '#c5c8ce'
                        return h('span', {
                            style: {
                                color: color
                            }
                        }, text)
                    }
                },
                {
                    title: '创建时间',
                    key: 'created_at',
                    align: 'center',
                    render: (h, params) => {
                        if (params.row.created_at) {
                            return h('span', this.formatDate(params.row.created_at))
                        }
                        return h('span', '--')
                    }
                },
                {
                    title: '操作',
                    slot: 'action',
                    align: 'center',
                    width: 120
                }
            ]
        }
    },
    created() {
        this.getEnterpriseInfo()
    },
    methods: {
        // 获取企业信息
        getEnterpriseInfo() {
            get_enterprise_info({ enterprise_auth_id: this.enterpriseAuthId || localStorage.getItem('enterprise_auth_id') || '' }).then(res => {
                if (res.code === 200 && res.data && res.data.enterprise_info) {
                    const info = res.data.enterprise_info
                    this.enterpriseInfo = {
                        company_name: info.prise_name,
                        legal_person: info.real_name
                    }
                    this.enterpriseAuthId = info.id
                    this.getMemberList()
                } else {
                    // 无企业信息则视为未认证
                    this.enterpriseInfo = {}
                    this.enterpriseAuthId = ''
                    this.total = 0
                    this.totalMembers = 0
                    this.activeMembers = 0
                    this.memberList = []
                }
            }).catch(err => {
                console.log('获取企业信息失败:', err)
                this.enterpriseInfo = {}
                this.enterpriseAuthId = ''
            })
        },
        // 获取成员列表
        getMemberList() {
            if (!this.enterpriseAuthId) return
            this.loading = true
            getMemberList({
                status: this.searchStatus,
                page: this.page,
                limit: this.pageSize,
                name: this.searchKeyword,
                enterprise_auth_id: this.enterpriseAuthId
            }).then(res => {
                if (res.code === 200 && res.data) {
                    const userList = res.data.user_list || {}
                    const rows = userList.data || []
                    this.memberList = rows
                    this.page = userList.current_page || this.page
                    this.pageSize = Number(userList.per_page || this.pageSize)
                    this.total = userList.total || 0
                    const statistics = res.data.statistics || {}
                    this.activeMembers = statistics.onthe_job || 0
                    this.totalMembers = (statistics.onthe_job || 0) + (statistics.resign || 0)
                }
            }).catch(err => {
                this.$Message.error(err.msg || '获取成员列表失败')
            }).finally(() => {
                this.loading = false
            })
        },
        // 搜索
        handleSearch() {
            this.page = 1
            this.getMemberList()
        },
        // 分页切换
        changePage(page) {
            this.page = page
            this.getMemberList()
        },
        // 离职操作
        resignMember(row) {
            this.$Modal.confirm({
                title: '确认离职',
                content: `确定要让成员 ${row.real_name} 离职吗？`,
                onOk: () => {
                    upMemberStatus({ member_id: row.id }).then(res => {
                        this.$Message.success(res.msg || '操作成功')
                        this.getMemberList()
                    }).catch(err => {
                        this.$Message.error(err.msg || '操作失败')
                    })
                }
            })
        },
        // 添加成员
        handleAddMember() {
            this.$refs.addMemberForm.validate((valid) => {
                if (valid) {
                    if (!this.enterpriseAuthId) return this.$Message.error('企业信息获取失败，请刷新重试')
                    addMember({
                        real_name: this.addMemberForm.name,
                        phone: this.addMemberForm.mobile,
                        enterprise_auth_id: this.enterpriseAuthId
                    }).then(res => {
                        this.$Message.success(res.msg || '添加成功')
                        this.showAddMember = false
                        this.addMemberForm = {
                            name: '',
                            mobile: ''
                        }
                        this.getMemberList()
                    }).catch(err => {
                        this.$Message.error(err.msg || '添加失败')
                    })
                }
            })
        },
        // 取消添加
        handleCancel() {
            this.addMemberForm = {
                name: '',
                mobile: ''
            }
            this.$refs.addMemberForm.resetFields()
        },
        // 格式化日期
        formatDate(dateStr) {
            if (!dateStr) return '--'
            const date = new Date(dateStr)
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }
    }
}
</script>

<style scoped lang="scss">
.page_wrap {
    width: 100%;
    min-height: 100%;
    background: #EDF0F9; 
}

.top-card {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
}

.nav_title {
    font-size: 18px;
    font-weight: bold;
    color: #17233d;
    margin-bottom: 20px;
}

.content-box {
    .enterprise-info {
        margin-bottom: 20px;
        padding-bottom: 20px;
        border-bottom: 1px solid #e8eaec;

        .enterprise-name {
            margin-bottom: 10px;
            font-size: 14px;
            color: #17233d;

            .label {
                color: #515a6e;
                margin-right: 8px;
            }

            .value {
                color: #17233d;
                font-weight: 500;
                margin-right: 20px;
            }

            .member-count {
                color: #808695;
                font-size: 13px;
            }
        }

        .legal-person {
            font-size: 14px;
            color: #17233d;

            .label {
                color: #515a6e;
                margin-right: 8px;
            }

            .value {
                color: #17233d;
            }
        }
    }

    .search-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: #fff;
        border-radius: 8px;
        margin-bottom: 16px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    }

    .table-header {
        margin-bottom: 20px;
    }
}

.required-mark {
    color: #ed4014;
    margin-right: 4px;
}
</style>
